import React from 'react';
import NotFoundPage from 'Routes/NotFoundPage';
import RouteLoading from 'Components/RouteLoading';
import { t } from 'i18next';
import { compose, createStore } from 'redux';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import { useConfigStore } from 'Hooks/zustand/useConfigStore';
import { useAuthenticationStore } from 'Hooks/zustand/useAuthenticationStore';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import Routes from './routes/Routes';
import { constructUser } from './utils';
import appConfigData from './store/config.json';
import rootReducer from './store';

/**
 * React Query
 * - staleTime: Prevent refetching data in the background.
 * - cacheTime: Garbage collected get determined by cacheTime, default is 5 minutes.
 * - refetchOnWindowFocus: If set to true, the query will refetch on window focus if the data is stale.
 *
 * @see https://tanstack.com/query/v4/docs/reference/QueryClient
 * @note If the query exists and the data is not invalidated or older than the given staleTime,
 * then the data from the cache will be returned. Otherwise it will try to fetch the latest data.
 */
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: Infinity,
            cacheTime: 1000 * 60 * 5,
            refetchOnWindowFocus: true,
            retry: 0
        }
    }
});

export default function App() {
    const { appConfig, authConfig, fetchConfig } = useConfigStore();
    const { authService, fetchUser, isAuthenticated, userInfo } = useAuthenticationStore();
    const { fetchAccessToken, getUserInfo, accessToken } = useAccessTokenStore.getState();
    const { error, fetchTemplate, template } = useTemplateStore();

    React.useEffect(() => {
        const id = 'dynatrace';
        if (!document.getElementById(id)) {
            const script = document.createElement('script');

            script.id = id;
            script.src = appConfig?.dynaTraceUrl;
            script.type = 'text/javascript';
            script.crossOrigin = 'anonymous';

            if (appConfig?.dynaTraceUrl) {
                document.head.appendChild(script);
            }
        }
    }, [appConfig]);

    React.useEffect(() => {
        let unsubscribe = false;

        const setConfig = async () => {
            if (!unsubscribe) {
                await fetchConfig();
            }
        };
        setConfig();

        return () => {
            unsubscribe = true;
        };
    }, [fetchConfig]);

    React.useEffect(() => {
        let unsubscribe = false;

        const userAuthentication = async () => {
            if (!unsubscribe && appConfig) {
                await fetchUser();
            }
        };
        userAuthentication();

        return () => {
            unsubscribe = true;
        };
    }, [fetchUser, appConfig]);

    React.useEffect(() => {
        let unsubscribe = false;

        if (!unsubscribe && authService) {
            fetchAccessToken();
        }

        return () => {
            unsubscribe = true;
        };
    }, [authService, fetchAccessToken]);

    React.useEffect(() => {
        let unsubscribe = false;

        const userTemplate = async () => {
            /**
             * Important note:
             * Start fetching Template after the user is Authenticated,
             * because the user access token is used by Axios interceptors.
             */
            if (!unsubscribe && isAuthenticated) {
                await fetchTemplate();
            }
        };
        userTemplate();

        return () => {
            unsubscribe = true;
        };
    }, [fetchTemplate, isAuthenticated]);

    /** If Template Repository ID is not configured show Error to customer service */
    if (error?.statusCode === 404) {
        return (
            <NotFoundPage
                content={{
                    heading: error.error,
                    body: t('TemplateNotFound'),
                    action: 'logout'
                }}
            />
        );
    }

    if (
        !accessToken ||
        !appConfig ||
        !authConfig ||
        !authService ||
        !isAuthenticated ||
        !template?.templateRepositoryId
    ) {
        return <RouteLoading />;
    }

    const userObj = constructUser(userInfo as any);

    const composeEnhancers = ((window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ as typeof compose) || compose;
    const store = createStore(
        rootReducer,
        {
            userReducer: { user: userObj },
            configReducer: { appInfo: { ...appConfig }, data: { ...appConfigData } }
        },
        composeEnhancers()
    );

    return (
        <HelmetProvider>
            <QueryClientProvider client={queryClient}>
                <Provider store={store}>
                    <Routes authConfig={authConfig} uniqueUserTokenId={getUserInfo().tenantKey} />
                </Provider>
                <ReactQueryDevtools position='bottom-right' initialIsOpen={false} />
            </QueryClientProvider>
        </HelmetProvider>
    );
}
